<script lang="ts" setup>
import { i18n } from '@/i18n';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IForecastTimelineTableItem } from '@/coreDomainsModule/entities/forecastTimeline';
import { computed, onMounted, ref, watch } from 'vue';
import { isError, isLoading, isNone, isSuccess, isZeroMatch, LoadingState } from '@/coreDomainsModule/uiLoadingState';
import loadingAnimation from '@/assets/loadingAnimation.json';
import { LottieAnimation } from 'lottie-web-vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { ForecastTimelineService } from '@/coreDomainsModule/services/forecastTimelineService';

/**
 * 内示遍歴テーブルのデータ用の型
 * [縦軸年][縦軸月][横軸年][横軸月] => 内示 | 販売実績
 */
type ForecastTimelineTableItemData = Record<number, Record<number, Record<number, Record<number, number | undefined>>>>;

const t = i18n.t;

const loadingState = ref<LoadingState>({ status: { type: 'loading' } });
const useTableItemData = ref<ForecastTimelineTableItemData>({});

// props
const props = defineProps<{
  headerMonthList: IYearMonth[];
  rowMonthList: IYearMonth[];
  tableItemData?: Set<IForecastTimelineTableItem>;
  propLoadingState: LoadingState;
}>();

const emits = defineEmits<{
  (e: 'reload'): void; // エラー発生時の再読み込み
}>();

watch(
  () => props.propLoadingState,
  (newValue) => {
    loadingState.value = newValue;
  }
);

watch(
  () => props.tableItemData,
  (newValue) => {
    if (newValue) setTableItemData(newValue);
  }
);

onMounted(() => {
  loadingState.value = props.propLoadingState;
});

/**
 * テーブルのデータを整形する
 * @param data - テーブルのデータ
 */
const setTableItemData = (data: Set<IForecastTimelineTableItem>) => {
  const tableItemData = data;
  const tableItemDataMap: ForecastTimelineTableItemData = {};

  /**
   * Recordオブジェクトを初期化する
   * @param year - 縦軸年
   * @param month - 縦軸月
   * @param horizontalYear - 横軸年
   * @param horizontalMonth - 横軸月
   */
  const _initRecords = (year: number, month: number, horizontalYear: number, horizontalMonth: number) => {
    if (!tableItemDataMap[year]) {
      tableItemDataMap[year] = {};
    }
    if (!tableItemDataMap[year][month]) {
      tableItemDataMap[year][month] = {};
    }
    if (!tableItemDataMap[year][month][horizontalYear]) {
      tableItemDataMap[year][month][horizontalYear] = {};
    }
    if (!tableItemDataMap[year][month][horizontalYear][horizontalMonth]) {
      tableItemDataMap[year][month][horizontalYear][horizontalMonth] = undefined;
    }
  };

  tableItemData.forEach((item) => {
    // 変数抽出
    const { verticalYearMonth, horizontalYearMonth, amount } = item;
    // レコードを初期化
    _initRecords(verticalYearMonth.year, verticalYearMonth.month, horizontalYearMonth.year, horizontalYearMonth.month);
    // データを格納
    tableItemDataMap[verticalYearMonth.year][verticalYearMonth.month][horizontalYearMonth.year][horizontalYearMonth.month] = amount;
  });

  useTableItemData.value = tableItemDataMap;
};

/**
 * テーブルの項目の種類を取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 */
const getTableCellItem = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth): number | undefined => {
  const tableItemData = useTableItemData.value;

  if (
    tableItemData[receivedYearMonth.year] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year] &&
    tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year][targetYearMonth.month]
  ) {
    return tableItemData[receivedYearMonth.year][receivedYearMonth.month][targetYearMonth.year][targetYearMonth.month];
  }

  return undefined;
};

/**
 * テーブルに表示するセルの内容を取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 */
const displayTableCell = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth) => {
  const tableItem = getTableCellItem(receivedYearMonth, targetYearMonth);
  return tableItem ?? '';
};

/**
 * テーブルのセルのクラスを取得する
 * @param receivedYearMonth - 受け取った年月
 * @param targetYearMonth - 表示する年月
 * @param headerIndex - ヘッダーのインデックス
 */
const getTableCellClass = (receivedYearMonth: IYearMonth, targetYearMonth: IYearMonth, headerIndex: number) => {
  const tableItemType = ForecastTimelineService.getTableCellType(receivedYearMonth, targetYearMonth);

  return {
    'timeline-table__row-cell--forecast': tableItemType === 'forecast',
    'timeline-table__row-cell--sales-result': tableItemType === 'salesResult',
    'timeline-table__row-cell--end-column': headerIndex === props.headerMonthList.length - 1,
  };
};
</script>

<template>
  <div class="customer-forecast-timeline__table-container content-container">
    <table class="content-container__timeline-table timeline-table">
      <thead>
        <tr class="timeline-table__header">
          <template v-if="isSuccess(loadingState.status)">
            <th
              v-for="i in 2"
              :key="i"
              class="timeline-table__header-cell timeline-table__header-cell--accessory timeline-table__header-cell--corner-sticky"
            >
              {{ '' }}
            </th>
            <template v-for="(month, index) in headerMonthList" :key="index">
              <th
                class="timeline-table__header-cell timeline-table__header-cell--month timeline-table__header-cell--sticky"
                :class="{ 'timeline-table__header-cell--end-column': index === headerMonthList.length - 1 }"
              >
                {{ DateService.convertIYearMonthToYYYYMMDivideSlash(month) }}
              </th>
            </template>
          </template>
          <th v-else>{{}}</th>
        </tr>
      </thead>
      <tbody>
        <template v-if="isLoading(loadingState.status)">
          <tr>
            <td class="loading-info" colspan="1000">
              <div class="loading-info__content">
                <LottieAnimation
                  v-if="isLoading(loadingState.status)"
                  ref="anim"
                  :animation-data="loadingAnimation"
                  :auto-play="true"
                  :loop="true"
                  :speed="1"
                />
                <SvgLoader v-else :class="`modal__icon`" name="" />
                <h1 class="loading-info__title">{{ t('データを読み込んでいます') }}</h1>
              </div>
            </td>
          </tr>
        </template>
        <template v-else-if="isZeroMatch(loadingState.status) || isNone(loadingState.status)">
          <tr>
            <td class="no-data" colspan="1000">
              {{ t('該当するデータはありません') }}
            </td>
          </tr>
        </template>
        <template v-else-if="isError(loadingState.status)">
          <tr>
            <td class="error" colspan="1000">
              {{ t('情報取得に失敗しました。時間を置いて再施行してください。') }}
              <button class="error__button" @click="() => emits('reload')">
                {{ t('再読込') }}
              </button>
            </td>
          </tr>
        </template>
        <template v-for="(receivedYearMonth, index) in rowMonthList" v-else :key="index">
          <tr class="timeline-table__row">
            <th
              v-if="index === 0"
              class="timeline-table__row-cell timeline-table__row-cell--span-title timeline-table__row-cell--sticky"
              :rowspan="rowMonthList.length"
            >
              {{ t('内示作成月') }}
            </th>
            <th class="timeline-table__row-cell timeline-table__row-cell--month timeline-table__row-cell--sticky">
              {{ DateService.convertIYearMonthToYYYYMMDivideSlash(receivedYearMonth) }}
            </th>
            <template v-for="(targetYearMonth, headerIndex) in headerMonthList" :key="headerIndex">
              <td class="timeline-table__row-cell" :class="getTableCellClass(receivedYearMonth, targetYearMonth, headerIndex)">
                {{ displayTableCell(receivedYearMonth, targetYearMonth) }}
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="scss">
.content-container {
  height: 100%;
  width: 100%;
  display: grid;
  gap: 16px;
  background-color: $surfaceDefault;
  border-radius: 4px;
  overflow: auto;
}

.timeline-table {
  border-collapse: collapse;
  font-size: $fontSizeS;
  font-weight: 400;
  line-height: 20px;

  &__header-cell {
    &--accessory {
      height: 40px;
      background-color: $surfaceSecondary;
    }

    &--month {
      height: 40px;
      text-align: end;
      background-color: $surfaceSecondary;
    }

    &--end-column {
      // 最後の列は右端にpaddingを追加している
      padding-right: 52px;
    }

    &--sticky {
      position: sticky;
      top: 0;
      left: 0;
      background: none;
      border-bottom: none;

      &:before {
        // スクロール時、sticky項目の裏に隙間ができるため、その隙間を埋めるための要素
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-bottom: 1px solid $borderDefault;
        background: $surfaceSecondary;
        z-index: -1;
      }
    }

    &--corner-sticky {
      position: sticky;
      top: 0;
      left: 0;
      background: none;
      border-bottom: none;
      z-index: 1;

      &:before {
        // スクロール時、sticky項目の裏に隙間ができるため、その隙間を埋めるための要素
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-bottom: 1px solid $borderDefault;
        background: $surfaceSecondary;
        z-index: -1;
      }
    }
  }

  &__row-cell {
    &--span-title {
      max-width: 20px;
      min-width: 20px;
      padding-top: 16px;
      padding-left: 4px;
      padding-right: 4px;
      border-bottom: none;
      border-right: 1px solid $borderDefault;
      writing-mode: vertical-rl;
      text-align: start;
    }

    &--month {
      min-width: 78px;
      max-width: 78px;
      height: 40px;
      padding-right: 8px;
      text-align: end;

      &:before {
        border-bottom: 1px solid $borderDefault;
      }
    }

    &--sticky {
      position: sticky;
      top: 0;
      left: 0;
      background: none;
      border-top: none;

      &:before {
        // スクロール時、sticky項目の裏に隙間ができるため、その隙間を埋めるための要素
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: $semanticWhite;
        z-index: -1;
      }
    }

    &--forecast {
      // 内示列のセル
      min-width: 112px;
      max-width: 112px;
      height: 40px;
      border-bottom: 1px solid $borderDefault;
      text-align: end;
      background-color: $semanticWhite;
    }

    &--sales-result {
      // 販売実績列のセル
      min-width: 112px; // border-spacingが付けられないため、幅指定で調整
      max-width: 112px;
      height: 40px;
      color: $semanticPrimary;
      font-weight: 700;
      border-bottom: 1px solid $borderDefault;
      text-align: end;
      background-color: $semanticWhite;
    }

    &--end-column {
      min-width: 164px; // border-spacingが付けられないため、幅指定で調整。最後の列は右端にpaddingを追加しているためその分も考慮
      max-width: 164px;
      padding-right: 52px;
    }
  }
}

// ローディングアイコン
.dli-loading-1 {
  display: inline-block;
  vertical-align: middle;
  color: #666;
  line-height: 1;
  width: 4em;
  height: 4em;
  border: 0.12em solid currentColor;
  border-top-color: rgba(102, 102, 102, 0.3);
  border-radius: 50%;
  box-sizing: border-box;
  animation: rotate 1s linear infinite;
}

.no-data {
  background-color: $surfaceDefault;
  color: $textSecondary;
  height: 477px;

  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  line-height: 18.2px;
  text-align: center;

  padding: 0;

  grid-column: 1 / -1;

  max-width: none;

  display: grid;
  place-items: center center;
}

.error {
  background-color: $semanticErrorSubdued;
  height: 477px;

  padding: 0;

  grid-column: 1 / -1;

  max-width: none;

  display: grid;
  place-items: center center;

  color: $semanticError;
  font-family: Noto Sans JP;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: center;

  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, auto);
  justify-content: center;

  &__button {
    color: $semanticPrimary;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    text-align: center;

    text-decoration: underline;
  }
}

.loading-info {
  height: 477px;
  padding: 0;
  grid-column: 1 / -1;
  max-width: none;
  display: grid;
  place-items: center;

  &__content {
    display: grid;
    place-items: center;
  }
}
</style>
